import {
  COMPETENCIES,
  KNOWLEDGE,
  MISCONCEPTIONS,
  type Attempt,
  type CompetencyCode,
  type KnowledgeCode,
  type MisconceptionCode,
  type Question,
} from '../types'

export type LearningTarget = {
  key: string
  knowledge: KnowledgeCode
  competency: CompetencyCode
  topic: string
  accuracy: number
  total: number
  incorrect: number
  highConfidenceMisses: number
  priority: number
  primaryMisconception?: MisconceptionCode
  nextReviewAt?: string
  reviewStage: number
}

export type MirrorSprint = {
  target: LearningTarget
  sourceQuestion?: Question
  questions: Question[]
  title: string
  rationale: string
}

const DAY = 24 * 60 * 60 * 1000
export const PENDING_MIRROR_TARGET_KEY = 'sangmath-pending-mirror-target'

export function queueMirrorSprint(target: LearningTarget) {
  sessionStorage.setItem(PENDING_MIRROR_TARGET_KEY, JSON.stringify(target))
}

const keyFor = (knowledge: KnowledgeCode, competency: CompetencyCode) => `${knowledge}:${competency}`

function sortedAttempts(attempts: Attempt[]) {
  return [...attempts].sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime())
}

function targetHistory(attempts: Attempt[], target: Pick<LearningTarget, 'knowledge' | 'competency'>) {
  return sortedAttempts(attempts).filter(a => a.knowledge.includes(target.knowledge) && a.competency.includes(target.competency))
}

export function reviewStage(attempts: Attempt[], target: Pick<LearningTarget, 'knowledge' | 'competency'>) {
  const history = targetHistory(attempts, target)
  const lastWrong = [...history].reverse().find(a => !a.correct)
  const relevant = lastWrong ? history.filter(a => new Date(a.at) >= new Date(lastWrong.at)) : history
  return Math.min(3, new Set(relevant.filter(a => a.correct).map(a => a.questionId)).size)
}

export function scheduleReview(attempt: Omit<Attempt, 'reviewDueAt'>, history: Attempt[]) {
  const target = { knowledge: attempt.knowledge[0], competency: attempt.competency[0] }
  if (!target.knowledge || !target.competency) return undefined
  if (!attempt.correct) return new Date(new Date(attempt.at).getTime() + DAY).toISOString()
  const stage = reviewStage([...history, attempt], target)
  if (stage >= 3) return undefined
  const delayDays = stage === 1 ? 3 : 7
  return new Date(new Date(attempt.at).getTime() + delayDays * DAY).toISOString()
}

/** Builds a priority list from behaviour, not a made-up predicted score. */
export function buildLearningTargets(attempts: Attempt[]): LearningTarget[] {
  const bucket = new Map<string, LearningTarget & { mistakes: Map<MisconceptionCode, number> }>()
  for (const attempt of attempts) {
    for (const knowledge of attempt.knowledge) for (const competency of attempt.competency) {
      const key = keyFor(knowledge, competency)
      const current = bucket.get(key) ?? {
        key, knowledge, competency, topic: attempt.topic, accuracy: 0, total: 0, incorrect: 0,
        highConfidenceMisses: 0, priority: 0, reviewStage: 0, mistakes: new Map<MisconceptionCode, number>(),
      }
      current.total += 1
      if (!attempt.correct) {
        current.incorrect += 1
        if (attempt.confidence === 'high') current.highConfidenceMisses += 1
        if (attempt.misconception) current.mistakes.set(attempt.misconception, (current.mistakes.get(attempt.misconception) ?? 0) + 1)
      }
      current.topic = attempt.topic || current.topic
      bucket.set(key, current)
    }
  }

  return [...bucket.values()].map(target => {
    const history = targetHistory(attempts, target)
  const reviewDates = history.map(a => a.reviewDueAt).filter((value): value is string => Boolean(value)).sort()
  const nextReviewAt = reviewDates[reviewDates.length - 1]
    const stage = reviewStage(attempts, target)
    const primaryMisconception = [...target.mistakes.entries()].sort((a, b) => b[1] - a[1])[0]?.[0]
    const accuracy = Math.round(((target.total - target.incorrect) / target.total) * 100)
    const dueWeight = nextReviewAt && new Date(nextReviewAt).getTime() <= Date.now() ? 25 : 0
    const priority = target.incorrect * 20 + target.highConfidenceMisses * 12 + (100 - accuracy) / 4 + dueWeight + (3 - stage) * 4
    return { ...target, accuracy, priority, primaryMisconception, nextReviewAt, reviewStage: stage }
  }).sort((a, b) => b.priority - a.priority)
}

export function getDueReviews(attempts: Attempt[], now = new Date()) {
  return buildLearningTargets(attempts).filter(target =>
    target.nextReviewAt && new Date(target.nextReviewAt).getTime() <= now.getTime() && target.reviewStage < 3,
  )
}

export function getMirrorSprint(questions: Question[], attempts: Attempt[], requested?: LearningTarget): MirrorSprint | null {
  const target = requested ?? buildLearningTargets(attempts).find(x => x.incorrect > 0 || x.nextReviewAt)
  if (!target) return null
  const history = targetHistory(attempts, target)
  const sourceAttempt = [...history].reverse().find(a => !a.correct) ?? history[history.length - 1]
  const sourceQuestion = sourceAttempt ? questions.find(q => q.id === sourceAttempt.questionId) : undefined
  const done = new Set(attempts.map(a => a.questionId))
  const candidates = questions.filter(question =>
    question.id !== sourceQuestion?.id &&
    question.knowledge.includes(target.knowledge) &&
    question.competency.includes(target.competency),
  )

  const rank = (question: Question) => {
    const unseen = done.has(question.id) ? 0 : 50
    const differentContext = sourceQuestion?.passageTitle && question.passageTitle !== sourceQuestion.passageTitle ? 25 : 0
    const authoredVariant = sourceQuestion && question.variantGroup === sourceQuestion.variantGroup ? 70 : 0
    const similarDifficulty = sourceQuestion ? 10 - Math.abs(question.difficulty - sourceQuestion.difficulty) * 2 : 0
    return unseen + differentContext + authoredVariant + similarDifficulty
  }
  const selected = [...candidates].sort((a, b) => rank(b) - rank(a)).slice(0, 3)
  if (!selected.length) return null

  const error = target.primaryMisconception ? MISCONCEPTIONS[target.primaryMisconception] : 'độ chính xác chưa ổn định'
  return {
    target,
    sourceQuestion,
    questions: selected,
    title: `Mirror Sprint: ${KNOWLEDGE[target.knowledge]}`,
    rationale: `3 câu cùng năng lực ${COMPETENCIES[target.competency]}, ưu tiên bối cảnh khác. Mục tiêu là sửa lỗi “${error}”, không học thuộc lại câu cũ.`,
  }
}

export function weeklyStudySeconds(attempts: Attempt[], now = new Date()) {
  const sevenDaysAgo = now.getTime() - 7 * DAY
  return attempts
    .filter(a => new Date(a.at).getTime() >= sevenDaysAgo)
    .reduce((sum, a) => sum + a.timeSeconds, 0)
}

export function labelForTarget(target: Pick<LearningTarget, 'knowledge' | 'competency'>) {
  return `${KNOWLEDGE[target.knowledge]} · ${COMPETENCIES[target.competency]}`
}
